<section
  class="banner"
  [class.banner--search-expanded]="showResults()"
  aria-label="IUDX Health hero"
>
  <div class="banner__media" aria-hidden="true"></div>

  @if (showResults()) {
    <div
      class="banner__search-overlay"
      role="presentation"
      (click)="closeResults()"
    ></div>
  }

  <div
    class="banner__content-wrapper"
    [class.banner__content-wrapper--search-open]="showResults()"
  >
    <div class="banner__inner flex w-full flex-col items-center text-center">
      <div class="banner__trusted-pill inline-flex items-center gap-2 px-4 py-2 font-medium">
        <img src="/icons/shield.svg" width="18" height="18" alt="" />
        <span>Trusted healthcare data exchange</span>
      </div>

      <h1 class="banner__title mt-6 max-w-4xl">
        Transforming Healthcare Through Trusted Data Exchange
      </h1>

      <p class="banner__subtitle mx-auto mt-4 max-w-3xl text-pretty">
        Seamlessly connect, share, and leverage healthcare data to drive
        innovation, improve patient outcomes, and enable smarter
        decision-making.
      </p>

      <div
        class="banner__search-container w-full"
        [class.banner__search-container--has-results]="showResults()"
      >
        <div class="banner__search-shell">
          <div
            class="banner__search-inner flex w-full min-w-0 flex-nowrap items-center gap-2 sm:gap-3"
          >
            <img
              class="banner__search-icon shrink-0"
              src="/icons/search.svg"
              width="20"
              height="20"
              alt=""
            />
            <input
              type="search"
              class="banner__search-input min-w-0 flex-1 border-0 bg-transparent outline-none"
              placeholder="Search datasets, models and usecases..."
              [ngModel]="searchQuery()"
              (ngModelChange)="onSearchQueryChange($event)"
              (focus)="onInputFocus()"
              (keyup.enter)="onSearch()"
              autocomplete="off"
              aria-label="Search catalogue"
              [attr.aria-expanded]="showResults()"
              aria-controls="banner-search-results"
            />
            <button
              type="button"
              class="banner__search-submit shrink-0 font-semibold"
              (click)="onSearch()"
            >
              @if (isLoading()) {
                <span class="banner__search-spinner" aria-hidden="true"></span>
              } @else {
                Search
              }
            </button>
          </div>
        </div>

        @if (showResults() && visibleResultsCount() > 0) {
          <div
            id="banner-search-results"
            class="banner__results banner-search-results"
            role="listbox"
            aria-label="Search suggestions"
          >
            @if (groupedResults().datasets.length > 0) {
              <div class="banner__results-section" role="group" aria-label="Datasets">
                <div class="banner__section-header">
                  <div class="banner__section-header-left">
                    <img
                      src="/icons/dataset.svg"
                      width="22"
                      height="22"
                      alt=""
                      class="banner__section-icon"
                    />
                    <span class="banner__section-title">Datasets</span>
                    <span class="banner__section-count">{{
                      groupedResults().datasets.length
                    }}</span>
                  </div>
                  <button
                    type="button"
                    class="banner__view-all"
                    (click)="viewAllResults('datasets')"
                  >
                    View All
                  </button>
                </div>
                @for (
                  result of groupedResults().datasets.slice(0, 2);
                  track result.id
                ) {
                  <button
                    type="button"
                    class="banner__result-row"
                    role="option"
                    (click)="navigateToAsset(result)"
                  >
                    <span class="banner__result-label">{{ result.label }}</span>
                    <span class="banner__result-chevron" aria-hidden="true">
                      <svg
                        width="7"
                        height="12"
                        viewBox="0 0 7 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1l5 5-5 5"
                          stroke="currentColor"
                          stroke-width="1.75"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                }
              </div>
            }

            @if (groupedResults().models.length > 0) {
              <div class="banner__results-section" role="group" aria-label="Models">
                <div class="banner__section-header">
                  <div class="banner__section-header-left">
                    <img
                      src="/icons/wire.svg"
                      width="22"
                      height="22"
                      alt=""
                      class="banner__section-icon"
                    />
                    <span class="banner__section-title">Models</span>
                    <span class="banner__section-count">{{
                      groupedResults().models.length
                    }}</span>
                  </div>
                  <button
                    type="button"
                    class="banner__view-all"
                    (click)="viewAllResults('models')"
                  >
                    View All
                  </button>
                </div>
                @for (
                  result of groupedResults().models.slice(0, 2);
                  track result.id
                ) {
                  <button
                    type="button"
                    class="banner__result-row"
                    role="option"
                    (click)="navigateToAsset(result)"
                  >
                    <span class="banner__result-label">{{ result.label }}</span>
                    <span class="banner__result-chevron" aria-hidden="true">
                      <svg
                        width="7"
                        height="12"
                        viewBox="0 0 7 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1l5 5-5 5"
                          stroke="currentColor"
                          stroke-width="1.75"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                }
              </div>
            }
          </div>
        }

        @if (
          showResults() &&
          visibleResultsCount() === 0 &&
          !isLoading() &&
          searchQuery().trim().length >= 3
        ) {
          <div
            id="banner-search-results"
            class="banner__results banner-search-results banner__results--empty"
            role="status"
          >
            <p class="banner__no-results">No results found.</p>
          </div>
        }
      </div>

      <div
        id="statistics-section"
        class="banner__stats-row grid w-full max-w-6xl"
      >
        <div class="banner__stat-card">
          <div class="banner__stat-icon-wrap flex shrink-0 items-center justify-center">
            <img src="/icons/dataset.svg" width="26" height="26" alt="" />
          </div>
          <div class="banner__stat-text text-left">
            <div class="banner__stat-number">{{ statsDatasets() }}</div>
            <div class="banner__stat-label">Datasets</div>
          </div>
        </div>
        <div class="banner__stat-card">
          <div class="banner__stat-icon-wrap flex shrink-0 items-center justify-center">
            <img src="/icons/brain.svg" width="26" height="26" alt="" />
          </div>
          <div class="banner__stat-text text-left">
            <div class="banner__stat-number">{{ statsModels() }}</div>
            <div class="banner__stat-label">Models</div>
          </div>
        </div>
        <div class="banner__stat-card">
          <div class="banner__stat-icon-wrap flex shrink-0 items-center justify-center">
            <img src="/icons/doctor.svg" width="26" height="26" alt="" />
          </div>
          <div class="banner__stat-text text-left">
            <div class="banner__stat-number">{{ statsUsecases() }}</div>
            <div class="banner__stat-label">Usecases</div>
          </div>
        </div>
        <div class="banner__stat-card">
          <div class="banner__stat-icon-wrap flex shrink-0 items-center justify-center">
            <img src="/icons/vector-1.svg" width="26" height="26" alt="" />
          </div>
          <div class="banner__stat-text text-left">
            <div class="banner__stat-number">{{ statsConcepts() }}</div>
            <div class="banner__stat-label">Concepts</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
